import React from 'react';
import {
    getResponseInitialState,
    getResponseProps,
    useAlert,
    useFetch
} from '../../hooks';
import {
    infoReducer,
    infoProps,
    infoInitialState,
    infoActionTypes
} from './info.reducer';
import { companyTypes, memberProps, useAuth } from '..';
import {
    PrispekPolicies,
    PrismaPolicies,
    SpektraPolicies
} from '../../constants';

interface InfoContextProps extends infoProps {
    info: infoProps;
    dispatchInfo: React.Dispatch<infoActionTypes>;
    getBlogs: () => Promise<void>;
    getBlogsResponse: getResponseProps;
    getPolicies: (company: companyTypes) => Promise<void>;
    getPoliciesResponse: getResponseProps;
    getEmployeePolicies: (employee: memberProps) => Promise<void>;
    getEmployeePoliciesResponse: getResponseProps;
}

export const InfoContext = React.createContext<InfoContextProps>({
    ...infoInitialState,
    info: infoInitialState,
    dispatchInfo: () => {},
    getBlogs: async () => {},
    getBlogsResponse: getResponseInitialState,
    getPolicies: async () => {},
    getPoliciesResponse: getResponseInitialState,
    getEmployeePolicies: async () => {},
    getEmployeePoliciesResponse: getResponseInitialState
});

export const useInfo = () => React.useContext(InfoContext);

export const InfoContextProvider: React.FC = ({ children }) => {
    const { session } = useAuth();
    const [info, dispatchInfo] = React.useReducer(
        infoReducer,
        infoInitialState
    );
    const {
        alert: blogAlert,
        dispatchAlert: dispatchBlogAlert,
        closeAlert: closeBlogAlert
    } = useAlert();
    const [getBlogsHandler, blogsLoading] = useFetch('/dbGetBlogs', 'GET');
    const {
        alert: policyAlert,
        dispatchAlert: dispatchPolicyAlert,
        closeAlert: closePolicyAlert
    } = useAlert();
    const [getPoliciesHandler, policiesLoading] = useFetch(
        '/dbViewMemberPolicies',
        'GET'
    );
    const [getEmployeePoliciesHandler, employeePoliciesLoading] = useFetch(
        '/dbGetEmployeePolicy',
        'GET'
    );

    const getBlogs = React.useCallback(async () => {
        const result = await getBlogsHandler();
        if (result.success) {
            dispatchInfo({ type: 'SET_BLOGS', blogs: result.data });
        } else {
            dispatchBlogAlert({
                type: 'UPDATE_ALERT',
                message: result.msg,
                severity: 'error'
            });
            dispatchBlogAlert({ type: 'OPEN' });
        }
    }, [dispatchBlogAlert, getBlogsHandler]);

    const getBlogsResponse = React.useMemo(
        () => ({
            loading: blogsLoading,
            alert: blogAlert,
            closeAlert: closeBlogAlert
        }),
        [blogAlert, blogsLoading, closeBlogAlert]
    );

    const getPolicies = React.useCallback(
        async company => {
            const result = await getPoliciesHandler({
                q: `member_guid=${session.MemberGuid}&&session_guid=${session.SessionGuid}`
            });
            if (!result.success) {
                dispatchPolicyAlert({
                    type: 'UPDATE_ALERT',
                    message: result.msg,
                    severity: 'error'
                });
                dispatchPolicyAlert({ type: 'OPEN' });
            } else {
                dispatchInfo({
                    type: 'SET_POLICIES',
                    policies:
                        company === 'PRISPEK'
                            ? PrispekPolicies
                            : company === 'PRISMA'
                            ? PrismaPolicies
                            : SpektraPolicies,
                    readPolicies: result.data
                });
            }
        },
        [
            getPoliciesHandler,
            session.MemberGuid,
            session.SessionGuid,
            dispatchPolicyAlert
        ]
    );

    const getPoliciesResponse = React.useMemo(() => {
        return {
            loading: policiesLoading,
            alert: policyAlert,
            closeAlert: closePolicyAlert
        };
    }, [policyAlert, policiesLoading, closePolicyAlert]);

    const getEmployeePolicies = React.useCallback(
        async (employee: memberProps) => {
            const result = await getEmployeePoliciesHandler({
                q: `member_guid=${session.MemberGuid}&&session_guid=${session.SessionGuid}&&employee_guid=${employee.Guid}`
            });
            if (result.success) {
                dispatchInfo({
                    type: 'SET_POLICIES',
                    policies:
                        employee.Company === 1
                            ? PrispekPolicies
                            : employee.Company === 0
                            ? PrismaPolicies
                            : SpektraPolicies,
                    readPolicies: result.data
                });
            } else {
                dispatchPolicyAlert({
                    type: 'UPDATE_ALERT',
                    message: result.msg,
                    severity: 'error'
                });
                dispatchPolicyAlert({ type: 'OPEN' });
            }
        },
        [
            getEmployeePoliciesHandler,
            session.MemberGuid,
            session.SessionGuid,
            dispatchPolicyAlert
        ]
    );

    const getEmployeePoliciesResponse = React.useMemo(() => {
        return {
            loading: employeePoliciesLoading,
            alert: policyAlert,
            closeAlert: closePolicyAlert
        };
    }, [policyAlert, employeePoliciesLoading, closePolicyAlert]);

    return (
        <InfoContext.Provider
            value={{
                ...info,
                info,
                dispatchInfo,
                getBlogs,
                getBlogsResponse,
                getPolicies,
                getPoliciesResponse,
                getEmployeePolicies,
                getEmployeePoliciesResponse
            }}
        >
            {children}
        </InfoContext.Provider>
    );
};
